import { useState, useEffect, useMemo, useRef } from 'react';
import DataTable from '../components/DataTable';
import HorizontalBarChart from '../components/HorizontalBarChart';
import LineChart from '../components/LineChart';
import KPICard from '../components/KPICard';
import { Activity, PhoneCall, AlertTriangle, MapPin } from 'lucide-react';
import PeriodSelector from '../components/PeriodSelector';
import { useUseCase } from '../contexts/UseCaseContext';
import { usePeriod } from '../contexts/PeriodContext';
import { useDemoMode } from '../contexts/DemoModeContext';
import { Button } from '@/components/ui/button';
import { logger } from '../utils/logger';
import { redactVehicleId, redactCounty, redactCallType } from '../utils/demoRedaction';
import { CHART_PALETTE } from '../config/colors';

interface IncidentCategoryData {
  title: string;
  xAxisLabel: string;
  yAxisLabel: string;
  timeframe: string;
  totalIncidents: number;
  data: Array<{
    label: string;
    values: Array<{ vehicle: string; value: number; color: string }>;
    count: number;
    percentage: number;
  }>;
}

interface IncidentCountyData {
  title: string;
  xAxisLabel: string;
  yAxisLabel: string;
  timeframe: string;
  threshold: number;
  totalIncidents: number;
  data: Array<{
    label: string;
    values: Array<{ vehicle: string; value: number; color: string }>;
    count: number;
    percentage: number;
  }>;
}

/** A row of a pumping CSV feed; headers differ between feeds. */
type PumpingRow = Record<string, string>;

/** Per-vehicle pumping summary from the summary feed. */
interface VehicleSummaryEntry {
  years: string;
  dailyAverage: string;
  weeklyAverage: string;
}

/** Accumulated pumping calls for one vehicle. */
interface VehicleCallAggregate {
  vehicle: string;
  years: string;
  calls: number;
  totalDuration: number;
  counties: Set<string>;
  dailyAverage: string;
  weeklyAverage: string;
}

export default function Incidents() {
  const { useCase } = useUseCase();
  const { selectedPeriod: contextSelectedPeriod } = usePeriod();
  const { isDemoMode } = useDemoMode();
  // ✅ Convert useCase to API serviceType: 'emergency' -> 'emergency_services', 'logistics' -> 'logistics'
  const serviceType = useCase === 'emergency' ? 'emergency_services' : 'logistics';
  
  // For Incidents tab, use "2020 - 2023" as the only period option for Emergency Services mode
  const incidentsPeriod = '2020 - 2023';
  const selectedPeriod = useCase === 'emergency' ? incidentsPeriod : contextSelectedPeriod;
  
  const [categoryData, setCategoryData] = useState<IncidentCategoryData | null>(null);
  const [countyData, setCountyData] = useState<IncidentCountyData | null>(null);
  const [loading, setLoading] = useState(true);
  const [, setError] = useState<string | null>(null);
  const [selectedYear, setSelectedYear] = useState<string>('all');
  const [selectedMonth, setSelectedMonth] = useState<string>('all');
  const [availableYears, setAvailableYears] = useState<number[]>([]);
  const [availableMonths, setAvailableMonths] = useState<Array<{year: number, month: number}>>([]);
  const [activeView, setActiveView] = useState<'category' | 'county'>('category');
  
  // Pumping data state
  const [pumpingSummary, setPumpingSummary] = useState<PumpingRow[]>([]);
  const [pumpingLocationCalls, setPumpingLocationCalls] = useState<PumpingRow[]>([]);
  const [, setPumpingMetadata] = useState<unknown>(null);
  const [pumpingLoading, setPumpingLoading] = useState(true);
  const [, setPumpingError] = useState<string | null>(null);
  
  // Pumping filter state
  const [pumpingSelectedYear, setPumpingSelectedYear] = useState<string>('all');
  const [pumpingSelectedMonth, setPumpingSelectedMonth] = useState<string>('all');
  const [pumpingAvailableYears, setPumpingAvailableYears] = useState<number[]>([]);
  const [pumpingAvailableMonths, setPumpingAvailableMonths] = useState<Array<{year: number, month: number}>>([]);
  const [pumpingCountyType, setPumpingCountyType] = useState<'incident' | 'response'>('incident');
  const [pumpingChartView, setPumpingChartView] = useState<'trips' | 'duration'>('trips');

  // Ref to track scroll position to prevent unwanted scrolling when dropdowns are clicked
  const scrollPositionRef = useRef<number>(0);

  // Auto-switch to available view if current view's data is not available
  useEffect(() => {
    if (activeView === 'category' && !categoryData && countyData) {
      setActiveView('county');
    } else if (activeView === 'county' && !countyData && categoryData) {
      setActiveView('category');
    }
  }, [categoryData, countyData, activeView]);
  
  // Determine interval based on selections:
  // - If Month is "All Months", use 'year' interval (yearly aggregate)
  // - If Month is specific, use 'month' interval (monthly aggregate)
  const selectedTimeInterval = selectedMonth === 'all' ? 'year' : 'month';
  
  // Fetch metadata on mount
  useEffect(() => {
    if (useCase !== 'emergency') {
      return;
    }
    
    import('../config/api').then(({ API_ENDPOINTS }) => {
      fetch(API_ENDPOINTS.incidentMetadata(serviceType))
        .then(res => {
          if (!res.ok) {
            throw new Error(`HTTP error! status: ${res.status}`);
          }
          return res.json();
        })
        .then((data: { years: number[], months: Array<{year: number, month: number}> }) => {
          setAvailableYears(data.years || []);
          setAvailableMonths(data.months || []);
        })
        .catch(err => {
          logger.error('[Incidents] Error loading metadata:', err);
          // Continue with empty arrays - dropdowns will only show "All Years" and "All Months"
        });
    });
  }, [useCase, serviceType]);

  useEffect(() => {
    // Only fetch data in Emergency Services mode
    if (useCase !== 'emergency') {
      setLoading(false);
      setError('Incidents tab is only available in Emergency Services mode');
      return;
    }

    // Don't set loading to true if we already have data - prevents flash
    // Only set loading on initial load
    if (!categoryData && !countyData) {
    setLoading(true);
    }
    setError(null);
    // Keep existing data visible - don't clear it

    let isCancelled = false;
    let categoryLoaded = false;
    let countyLoaded = false;

    const checkComplete = () => {
      if (categoryLoaded && countyLoaded && !isCancelled) {
        setLoading(false);
      }
    };

    if (!selectedPeriod) {
      setLoading(false);
      return;
    }

    import('../config/api').then(({ API_ENDPOINTS }) => {
      // Build query parameters
      let yearParam = selectedYear;
      const monthParam = selectedMonth;
      
      if (selectedMonth !== 'all') {
        const selectedMonthData = availableMonths.find(m => m.month.toString() === selectedMonth);
        if (selectedMonthData) {
          yearParam = selectedMonthData.year.toString();
        }
      }
      
      const queryParams = new URLSearchParams({
        interval: selectedTimeInterval,
        year: yearParam,
        month: monthParam
      });
      
      // Fetch category distribution
      const categoryUrl = `${API_ENDPOINTS.incidentCategoryDistribution(selectedPeriod, serviceType)}?${queryParams.toString()}`;
      fetch(categoryUrl)
        .then(res => {
          if (!res.ok) {
            throw new Error(`HTTP error! status: ${res.status}`);
          }
          return res.json();
        })
        .then((data: IncidentCategoryData) => {
          if (isCancelled) return;
          logger.debug('[Incidents] Loaded category distribution:', data);
          setCategoryData(data); // Update directly - old data was already visible
          categoryLoaded = true;
          checkComplete();
        })
        .catch(err => {
          if (isCancelled) return;
          logger.error('[Incidents] Error loading category distribution:', err);
          // Don't clear data on error - keep showing old data
          categoryLoaded = true;
          checkComplete();
        });

      // Fetch county distribution
      const countyUrl = `${API_ENDPOINTS.incidentCountyDistribution(selectedPeriod, serviceType)}?${queryParams.toString()}`;
      fetch(countyUrl)
        .then(res => {
          if (!res.ok) {
            throw new Error(`HTTP error! status: ${res.status}`);
          }
          return res.json();
        })
        .then((data: IncidentCountyData) => {
          if (isCancelled) return;
          logger.debug('[Incidents] Loaded county distribution:', data);
          setCountyData(data); // Update directly - old data was already visible
          countyLoaded = true;
          checkComplete();
        })
        .catch(err => {
          if (isCancelled) return;
          logger.error('[Incidents] Error loading county distribution:', err);
          // Don't clear data on error - keep showing old data
          countyLoaded = true;
          checkComplete();
        });
    });

    return () => {
      isCancelled = true;
    };
  }, [useCase, serviceType, selectedPeriod, selectedTimeInterval, selectedYear, selectedMonth]);

  // Helper function to parse CSV
  const parseCSVLine = (line: string): string[] => {
    const values: string[] = [];
    let currentValue = '';
    let inQuotes = false;
    
    for (let charIdx = 0; charIdx < line.length; charIdx++) {
      const char = line[charIdx];
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        values.push(currentValue.trim());
        currentValue = '';
      } else {
        currentValue += char;
      }
    }
    values.push(currentValue.trim());
    return values;
  };

  const parseCSV = (text: string): PumpingRow[] => {
    const lines = text.trim().split(/\r?\n/).filter(line => line.trim() !== '');
    if (lines.length < 2) return [];
    
    const headers = parseCSVLine(lines[0]);
    const rows: PumpingRow[] = [];
    
    for (let i = 1; i < lines.length; i++) {
      const values = parseCSVLine(lines[i]);
      const row: PumpingRow = {};
      headers.forEach((header, index) => {
        row[header] = values[index] || '';
      });
      rows.push(row);
    }
    
    return rows;
  };

  // Format number with commas
  const formatNumber = (num: number | string): string => {
    const n = typeof num === 'string' ? parseFloat(num) : num;
    if (isNaN(n)) return '0';
    return n.toLocaleString('en-US', { maximumFractionDigits: 1 });
  };


  // Helper function to generate timeframe string based on selections
  const getTimeframeString = (): string => {
    if (selectedMonth !== 'all') {
      // Specific month selected - need to find the year from availableMonths
      // When month is selected, we need to find which year it belongs to
      let monthYear = selectedYear;
      if (selectedYear === 'all') {
        // If year is 'all' but month is selected, find the first matching month
        const selectedMonthData = availableMonths.find(m => m.month.toString() === selectedMonth);
        if (selectedMonthData) {
          monthYear = selectedMonthData.year.toString();
        }
      }
      if (monthYear && monthYear !== 'all') {
        const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
        return `${monthNames[parseInt(selectedMonth) - 1]} ${monthYear}`;
      }
    } else if (selectedYear !== 'all') {
      // Specific year selected
      return selectedYear;
    } else if (selectedMonth === 'all' && selectedYear === 'all') {
      // All data - use year aggregate
      return 'All Years';
    }
    return '';
  };

  // Helper function to calculate number of days in the selected timeframe
  const getDaysInTimeframe = (): number => {
    if (selectedMonth !== 'all') {
      // Specific month selected
      let monthYear = selectedYear;
      if (selectedYear === 'all') {
        const selectedMonthData = availableMonths.find(m => m.month.toString() === selectedMonth);
        if (selectedMonthData) {
          monthYear = selectedMonthData.year.toString();
        }
      }
      if (monthYear && monthYear !== 'all') {
        const year = parseInt(monthYear);
        const month = parseInt(selectedMonth);
        // Get days in the specific month
        return new Date(year, month, 0).getDate();
      }
    } else if (selectedYear !== 'all') {
      // Specific year selected - calculate days in that year
      const year = parseInt(selectedYear);
      return ((year % 4 === 0 && year % 100 !== 0) || year % 400 === 0) ? 366 : 365;
    } else if (selectedMonth === 'all' && selectedYear === 'all') {
      // All Years - calculate from available years
      if (availableYears.length > 0) {
        const minYear = Math.min(...availableYears);
        const maxYear = Math.max(...availableYears);
        let totalDays = 0;
        for (let year = minYear; year <= maxYear; year++) {
          totalDays += ((year % 4 === 0 && year % 100 !== 0) || year % 400 === 0) ? 366 : 365;
        }
        return totalDays;
      }
      // Fallback: assume 4 years (2020-2023) = ~1461 days
      return 1461;
    }
    // Default fallback
    return 365;
  };

  // Helper function to generate business-focused titles
  const getBusinessTitle = (dataType: 'category' | 'county', originalTitle: string): string => {
    const timeframe = getTimeframeString();
    
    if (!timeframe) {
      // If no timeframe, return base title
      if (dataType === 'category') {
        return 'Top 10 Most Common Call Types';
      } else if (dataType === 'county') {
        return 'Top 10 Busiest Areas by Incident Volume';
      }
    }
    
    if (dataType === 'category') {
      // Focus on what types of calls are most common - helps with resource planning
      return `Top 10 Most Common Call Types (${timeframe})`;
    } else if (dataType === 'county') {
      // Focus on where incidents are happening - helps with station placement
      return `Top 10 Busiest Areas by Incident Volume (${timeframe})`;
    }
    
    // Fallback to original if needed
    const baseTitle = originalTitle.replace(/\s*\([^)]*\)\s*$/, '').trim();
    return `${baseTitle} (${timeframe})`;
  };

  // Fetch pumping data
  useEffect(() => {
    if (useCase !== 'emergency') {
      setPumpingLoading(false);
      return;
    }

    setPumpingLoading(true);
    setPumpingError(null);
    setPumpingSummary([]);
    setPumpingLocationCalls([]);
    setPumpingMetadata(null);

    let isCancelled = false;

    import('../config/api').then(({ API_ENDPOINTS }) => {
      // Fetch pumping summary CSV
      fetch(API_ENDPOINTS.pumpingSummary(serviceType))
        .then(res => {
          if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
          return res.text();
        })
        .then(text => {
          if (isCancelled) return;
          const data = parseCSV(text);
          setPumpingSummary(data);
          logger.debug('[Pumping] Loaded summary:', data);
        })
        .catch(err => {
          if (isCancelled) return;
          logger.error('[Pumping] Error loading summary:', err);
        });

      // Fetch pumping summary JSON for metadata
      fetch(API_ENDPOINTS.pumpingSummaryJSON(serviceType))
        .then(res => {
          if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
          return res.json();
        })
        .then(data => {
          if (isCancelled) return;
          setPumpingMetadata(data.metadata || null);
          logger.debug('[Pumping] Loaded metadata:', data.metadata);
        })
        .catch(err => {
          if (isCancelled) return;
          logger.error('[Pumping] Error loading metadata:', err);
        });

      // Fetch location calls
      fetch(API_ENDPOINTS.pumpingLocationCalls(serviceType))
        .then(res => {
          if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
          return res.text();
        })
        .then(text => {
          if (isCancelled) return;
          const data = parseCSV(text);
          setPumpingLocationCalls(data);
          logger.debug('[Pumping] Loaded location calls:', data);
        })
        .catch(err => {
          if (isCancelled) return;
          logger.error('[Pumping] Error loading location calls:', err);
        })
        .finally(() => {
          if (!isCancelled) {
            setPumpingLoading(false);
          }
        });
    });

    return () => {
      isCancelled = true;
    };
  }, [useCase, serviceType]);


  // Prepare incident data for display
  // For incident data, we need to get years and averages from the summary data since location calls don't have years
  const incidentData = useMemo(() => {
    // Create maps of vehicle to summary data
    const vehicleSummaryMap = new Map<string, VehicleSummaryEntry>();
    pumpingSummary.forEach(row => {
      const vehicle = row.Vehicle_Index || row.vehicle_index || '';
      if (vehicle) {
        vehicleSummaryMap.set(vehicle, {
          years: row.Years || row.years || 'N/A',
          dailyAverage: parseFloat(row.daily_average_count || '0').toFixed(1),
          weeklyAverage: parseFloat(row.weekly_average_count || '0').toFixed(1),
        });
      }
    });
    
    // Group by vehicle and calculate aggregates
    const vehicleMap = new Map<string, VehicleCallAggregate>();
    
    pumpingLocationCalls.forEach(row => {
      const vehicle = row.Vehicle_Index || row.vehicle_index || '';
      if (!vehicle) return;
      
      if (!vehicleMap.has(vehicle)) {
        const summary = vehicleSummaryMap.get(vehicle) || { years: 'N/A', dailyAverage: '0', weeklyAverage: '0' };
        vehicleMap.set(vehicle, {
          vehicle,
          years: summary.years,
          calls: 0,
          totalDuration: 0,
          counties: new Set<string>(),
          dailyAverage: summary.dailyAverage,
          weeklyAverage: summary.weeklyAverage,
        });
      }
      
      const entry = vehicleMap.get(vehicle)!;
      const calls = parseInt(row.num_calls || '0');
      const avgDuration = parseFloat(row.pump_dur_per_incident || '0');
      const county = row.Incident_County || row.incident_county || 'Unknown';
      
      entry.calls += calls;
      entry.totalDuration += calls * avgDuration;
      entry.counties.add(county);
    });
    
    return Array.from(vehicleMap.values()).map(entry => {
      const avgDurationMins = entry.calls > 0 ? entry.totalDuration / entry.calls : 0;
      
      return {
        vehicle: redactVehicleId(entry.vehicle, isDemoMode),
        _vehicle_sortKey: entry.vehicle, // For sorting/export (keep original for internal use)
        years: entry.years,
        calls: entry.calls,
        avgDuration: `${avgDurationMins.toFixed(0)} mins`,
        dailyAverage: entry.dailyAverage,
        weeklyAverage: entry.weeklyAverage,
      };
    }).sort((a, b) => b.calls - a.calls);
  }, [pumpingLocationCalls, pumpingSummary]);

  // Extract available years and months from pumping data
  useEffect(() => {
    if (pumpingLocationCalls.length === 0) {
      setPumpingAvailableYears([]);
      setPumpingAvailableMonths([]);
      return;
    }

    const yearsSet = new Set<number>();
    const monthsSet = new Set<string>();
    
    pumpingLocationCalls.forEach(row => {
      // Use Year and Month columns directly from CSV
      const year = row.Year ? parseInt(row.Year) : null;
      const month = row.Month ? parseInt(row.Month) : null;
      
      if (year && !isNaN(year)) {
        yearsSet.add(year);
      }
      
      if (year && month && !isNaN(year) && !isNaN(month)) {
        monthsSet.add(`${year}-${month}`);
      }
    });

    const years = Array.from(yearsSet).sort();
    const months = Array.from(monthsSet)
      .map(m => {
        const [y, mo] = m.split('-').map(Number);
        return { year: y, month: mo };
      })
      .sort((a, b) => {
        if (a.year !== b.year) return a.year - b.year;
        return a.month - b.month;
      });

    setPumpingAvailableYears(years);
    setPumpingAvailableMonths(months);
  }, [pumpingLocationCalls]);

  // Prepare county bar chart data filtered by year/month
  const pumpingCountyData = useMemo(() => {
    if (pumpingLocationCalls.length === 0) return [];

    // Filter by year and month using Year and Month columns
    let filteredData = pumpingLocationCalls;
    
    if (pumpingSelectedYear !== 'all') {
      const year = parseInt(pumpingSelectedYear);
      filteredData = filteredData.filter(row => {
        const rowYear = row.Year ? parseInt(row.Year) : null;
        return rowYear === year;
      });
    }

    if (pumpingSelectedMonth !== 'all') {
      const [year, month] = pumpingSelectedMonth.split('-').map(Number);
      filteredData = filteredData.filter(row => {
        const rowYear = row.Year ? parseInt(row.Year) : null;
        const rowMonth = row.Month ? parseInt(row.Month) : null;
        return rowYear === year && rowMonth === month;
      });
    }

    // Count by county (use selected county type)
    const countyCounts = new Map<string, number>();
    filteredData.forEach(row => {
      let county: string;
      if (pumpingCountyType === 'incident') {
        county = row.Incident_County || row.incident_county || 'Unknown';
      } else {
        county = row.Response_County || row.response_county || 'Unknown';
      }
      const calls = parseInt(row.num_calls || row.num_Calls || '0');
      countyCounts.set(county, (countyCounts.get(county) || 0) + calls);
    });

    // Convert to bar chart format
    const sortedCounties = Array.from(countyCounts.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10); // Top 10 counties

    const total = sortedCounties.reduce((sum, [, count]) => sum + count, 0);
    
    // Tab10 color palette (matplotlib tab10)
    const colors = CHART_PALETTE;

      return sortedCounties.map(([county, count], index) => {
      const redactedCounty = redactCounty(county, isDemoMode);
      return {
        label: redactedCounty,
        _county_sortKey: county, // For sorting/export (keep original for internal use)
      values: [{
          vehicle: redactedCounty,
        value: count,
        color: colors[index % colors.length]
      }],
      count: count,
      percentage: total > 0 ? (count / total) * 100 : 0
      };
    });
  }, [pumpingLocationCalls, pumpingSelectedYear, pumpingSelectedMonth, pumpingCountyType]);


  // Compute whether to show Pumping Activity section - ensures it's hidden during loading transitions
  const showPumpingActivity = useMemo(() => {
    if (useCase !== 'emergency') {
      return true; // Always show in non-emergency mode
    }
    // In emergency mode, only show when not loading AND we have data
    return !loading && (categoryData || countyData);
  }, [useCase, loading, categoryData, countyData]);

  return (
    <div className="space-y-8">
      <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between mb-4">
        <div>
          <h2 className="text-2xl font-bold text-white mb-2">Incidents</h2>
          <p className="text-muted-foreground text-sm">
            Monitor emergency operations and incident patterns. Helps plan where to place resources and improve response times.
          </p>
        </div>
        <div className="flex items-center gap-4">
          {useCase === 'emergency' ? (
            // Custom period selector for Incidents tab - only show "2020 - 2023"
            <div className="flex items-center gap-3">
              <label htmlFor="incidents-period-selector" className="text-sm text-muted-foreground">Period:</label>
              <select
                id="incidents-period-selector"
                value={incidentsPeriod}
                disabled
                className="px-3 py-1.5 text-sm font-medium rounded-md transition-all border bg-white text-black border-warning shadow-lg shadow-warning/50 cursor-not-allowed"
                style={{
                  appearance: 'none',
                  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23000' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`,
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'right 0.75rem center',
                  paddingRight: '2.5rem'
                }}
              >
                <option value={incidentsPeriod} className="bg-card text-muted-foreground">
                  {incidentsPeriod}
                </option>
              </select>
            </div>
          ) : (
            <PeriodSelector />
          )}
        </div>
      </div>

      {/* KPI Cards with Time Series Chart */}
      {useCase === 'emergency' && !loading && (categoryData || countyData || pumpingSummary.length > 0) && (
        <div className="grid grid-cols-3 md:grid-cols-5 gap-4 mb-8" style={{ gridAutoRows: '1fr' }}>
          {/* 2x2 KPI Cards Grid */}
          {categoryData && categoryData.data && categoryData.data.length > 0 && (
            <KPICard
              title="Highest Call Type"
              value={redactCallType(categoryData.data[0]?.label || 'N/A', isDemoMode)}
              subtitle={`${categoryData.data[0]?.count?.toLocaleString() || 0} incidents`}
              icon={AlertTriangle}
            />
          )}
          {countyData && countyData.data && countyData.data.length > 0 && (
              <KPICard
                title="Busiest Area"
                value={redactCounty(countyData.data[0]?.label || 'N/A', isDemoMode)}
                subtitle={`${countyData.data[0]?.count?.toLocaleString() || 0} incidents`}
                icon={MapPin}
              />
          )}
          {pumpingSummary.length > 0 && (
            <>
              <div style={{ gridRow: '2' }}>
                <KPICard
                  title="Total Pumping Events"
                  value={formatNumber(pumpingSummary.reduce((sum, row) => 
                    sum + parseInt(row.total_pumping_count || '0'), 0))}
                  subtitle="Total period"
                  icon={Activity}
                />
              </div>
              <div style={{ gridRow: '2' }}>
                <KPICard
                  title="Avg Daily Calls"
                  value={(() => {
                    // Calculate from all incidents (prefer categoryData, fallback to countyData)
                    const totalIncidents = categoryData?.totalIncidents || countyData?.totalIncidents || 0;
                    if (totalIncidents > 0) {
                      const days = getDaysInTimeframe();
                      const avgDaily = totalIncidents / days;
                      return avgDaily.toFixed(1);
                    }
                    // Fallback to pumping data if no incident data available
                    if (pumpingSummary.length > 0) {
                      return (pumpingSummary.reduce((sum, row) => 
                        sum + parseFloat(row.daily_average_count || '0'), 0) / pumpingSummary.length).toFixed(1);
                    }
                    return '0';
                  })()}
                  subtitle={categoryData?.totalIncidents || countyData?.totalIncidents 
                    ? "All incidents average" 
                    : "Per vehicle average"}
                  icon={PhoneCall}
                />
              </div>
            </>
          )}
          
          {/* Time Series Chart - Pumping Trips / Duration */}
          {pumpingLocationCalls.length > 0 && (() => {
            // Aggregate data by Year-Month, filter for 2022 only
            const monthlyData = new Map<string, { calls: number; totalDuration: number; count: number }>();
            pumpingLocationCalls.forEach(row => {
              const year = row.Year ? parseInt(row.Year) : null;
              const month = row.Month ? parseInt(row.Month) : null;
              // Only include data from 2022
              if (year === 2022 && month && !isNaN(month)) {
                const key = `${year}-${String(month).padStart(2, '0')}`;
                const calls = parseInt(row.num_calls || '0');
                const avgDuration = parseFloat(row.pump_dur_per_incident || '0');
                const totalDuration = calls * avgDuration; // Total duration for this month
                
                if (!monthlyData.has(key)) {
                  monthlyData.set(key, { calls: 0, totalDuration: 0, count: 0 });
                }
                const entry = monthlyData.get(key)!;
                entry.calls += calls;
                entry.totalDuration += totalDuration;
                entry.count += 1; // Count of rows for this month (for averaging)
              }
            });
            
            // Convert to array and sort by date
            const chartData = Array.from(monthlyData.entries())
              .map(([key, data]) => {
                const [year, month] = key.split('-').map(Number);
                const date = new Date(year, month - 1, 1);
                // Remove year from label - just show month name
                const monthName = date.toLocaleDateString('en-GB', { month: 'short' });
                
                // Calculate value based on view mode
                const value = pumpingChartView === 'trips' 
                  ? data.calls 
                  : data.calls > 0 ? data.totalDuration / data.calls : 0; // Average duration in minutes
                
                return {
                  label: monthName,
                  value: value,
                  date: key
                };
              })
              .sort((a, b) => a.date.localeCompare(b.date));
            
            if (chartData.length > 0) {
              const chartTitle = pumpingChartView === 'trips' 
                ? "Monthly Pumping Trips (2022)" 
                : "Monthly Pumping Duration (2022)";
              
              const yAxisLabel = pumpingChartView === 'trips' 
                ? "No. of Pumping Calls" 
                : "Duration (mins)";
              
              // Use different colors for different metrics: red for trips, orange for duration
              const chartColor = pumpingChartView === 'trips' ? CHART_PALETTE[3] : CHART_PALETTE[1];
              
              return (
                <div className="col-span-3 row-span-2 h-full relative" key="pumping-timeseries">
                  {/* Dropdown for switching time series */}
                  <div className="absolute top-4 right-4 z-10">
                    <select
                      value={pumpingChartView}
                      onChange={(e) => setPumpingChartView(e.target.value as 'trips' | 'duration')}
                      className="px-3 py-1.5 text-sm font-medium rounded-md transition-all border bg-secondary text-muted-foreground border-accent hover:bg-accent hover:border-warning hover:text-warning hover:shadow-lg hover:shadow-warning/50 focus:outline-none focus:ring-2 focus:ring-warning focus:border-warning cursor-pointer"
                      style={{
                        appearance: 'none',
                        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%239ca3af' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`,
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'right 0.75rem center',
                        paddingRight: '2.5rem'
                      }}
                    >
                      <option value="trips" className="bg-card text-muted-foreground">Pumping Trips</option>
                      <option value="duration" className="bg-card text-muted-foreground">Pumping Duration</option>
                    </select>
                  </div>
                  
                  <LineChart
                    title={chartTitle}
                    data={chartData}
                    yAxisLabel={yAxisLabel}
                    color={chartColor}
                    heightClass="min-h-80"
                    chartHeight={265}
                    defaultZoomed
                    formatValueAsInteger={pumpingChartView === 'trips'}
          />
        </div>
              );
            }
            return null;
          })()}
        </div>
      )}

      {/* Incident Distribution Charts */}
      {useCase === 'emergency' && (
        <>
          {loading && (
            <div className="text-center py-8">
              <div className="flex flex-col items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-warning mb-4"></div>
                <p className="text-muted-foreground">Loading incident data...</p>
              </div>
            </div>
          )}

          {!loading && (categoryData || countyData) && (
            <section className="mt-8 mb-8">
              {/* Conditionally render active chart */}
              {activeView === 'category' && categoryData && (
                <>
                  <div>
                    <div className="flex items-center justify-between mb-6">
                      <div>
                        <h3 className="text-xl font-bold text-white mb-2">{getBusinessTitle('category', categoryData.title)}</h3>
                        <p className="text-muted-foreground text-sm">
                          See which types of calls you're responding to most often. Use this to plan training, equipment needs, and crew assignments.
                        </p>
                      </div>
                    </div>
                    {/* Filters and Toggle Buttons */}
                    <div className="flex items-center gap-4 mb-4">
                        {/* Year and Month Dropdowns */}
                        <div className="flex items-center gap-4">
                          <div className="flex items-center gap-2">
                            <label className="text-sm font-medium text-muted-foreground">Year:</label>
                            <select
                              value={selectedYear}
                              onChange={(e) => setSelectedYear(e.target.value)}
                              onMouseDown={() => {
                                // Save current scroll position before dropdown opens
                                scrollPositionRef.current = window.scrollY || window.pageYOffset || document.documentElement.scrollTop || 0;
                              }}
                              onBlur={() => {
                                // Only restore scroll if it jumped to the top unexpectedly
                                // Check if current scroll is near top (within 100px) but we were scrolled down
                                const currentScroll = window.scrollY || window.pageYOffset || document.documentElement.scrollTop || 0;
                                const savedScroll = scrollPositionRef.current;
                                
                                // Only restore if we were scrolled down significantly and now we're near the top
                                if (savedScroll > 200 && currentScroll < 100) {
                                  // Restore the saved scroll position
                                  requestAnimationFrame(() => {
                                    window.scrollTo(0, savedScroll);
                                  });
                                }
                              }}
                              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all border cursor-pointer ${
                                selectedYear !== 'all'
                                  ? 'bg-white text-black border-warning shadow-lg shadow-warning/50'
                                  : 'bg-secondary text-muted-foreground border-accent hover:bg-accent hover:border-warning hover:text-warning hover:shadow-lg hover:shadow-warning/50'
                              } focus:outline-none focus:ring-2 focus:ring-warning focus:border-warning`}
                              style={{
                                appearance: 'none',
                                backgroundImage: selectedYear !== 'all'
                                  ? `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23000' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`
                                  : `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%239ca3af' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`,
                                backgroundRepeat: 'no-repeat',
                                backgroundPosition: 'right 0.75rem center',
                                paddingRight: '2.5rem'
                              }}
                            >
                              <option value="all" className="bg-card text-muted-foreground">All Years</option>
                              {availableYears.map(year => (
                                <option key={year} value={year.toString()} className="bg-card text-muted-foreground">
                                  {year}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div className="flex items-center gap-2">
                            <label className="text-sm font-medium text-muted-foreground">Month:</label>
                            <select
                              value={selectedMonth}
                              onChange={(e) => setSelectedMonth(e.target.value)}
                              onMouseDown={() => {
                                // Save current scroll position before dropdown opens
                                scrollPositionRef.current = window.scrollY || window.pageYOffset || document.documentElement.scrollTop || 0;
                              }}
                              onBlur={() => {
                                // Only restore scroll if it jumped to the top unexpectedly
                                // Check if current scroll is near top (within 100px) but we were scrolled down
                                const currentScroll = window.scrollY || window.pageYOffset || document.documentElement.scrollTop || 0;
                                const savedScroll = scrollPositionRef.current;
                                
                                // Only restore if we were scrolled down significantly and now we're near the top
                                if (savedScroll > 200 && currentScroll < 100) {
                                  // Restore the saved scroll position
                                  requestAnimationFrame(() => {
                                    window.scrollTo(0, savedScroll);
                                  });
                                }
                              }}
                              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all border cursor-pointer ${
                                selectedMonth !== 'all'
                                  ? 'bg-white text-black border-warning shadow-lg shadow-warning/50'
                                  : 'bg-secondary text-muted-foreground border-accent hover:bg-accent hover:border-warning hover:text-warning hover:shadow-lg hover:shadow-warning/50'
                              } focus:outline-none focus:ring-2 focus:ring-warning focus:border-warning`}
                              style={{
                                appearance: 'none',
                                backgroundImage: selectedMonth !== 'all'
                                  ? `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23000' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`
                                  : `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%239ca3af' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`,
                                backgroundRepeat: 'no-repeat',
                                backgroundPosition: 'right 0.75rem center',
                                paddingRight: '2.5rem'
                              }}
                            >
                              <option value="all" className="bg-card text-muted-foreground">All Months</option>
                              {availableMonths
                                .filter(m => selectedYear === 'all' || m.year.toString() === selectedYear)
                                .map(m => {
                                  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
                                  return (
                                    <option key={`${m.year}-${m.month}`} value={m.month.toString()} className="bg-card text-muted-foreground">
                                      {monthNames[m.month - 1]} {m.year}
                                    </option>
                                  );
                                })}
                            </select>
                          </div>
                        </div>
                        {/* Toggle Buttons */}
                        <div className="flex items-center gap-2 ml-auto">
                          <Button
                            type="button"
                            variant="secondary"
                            size="md"
                            onClick={() => setActiveView('category')}
                            disabled={!categoryData}
                            className={`border-accent text-muted-foreground hover:bg-accent hover:border-warning hover:text-warning hover:shadow-lg hover:shadow-warning/50 ${
                              activeView === 'category'
                                ? 'bg-white text-black border-warning shadow-lg shadow-warning/50'
                                : ''
                            }`}
                          >
                            Call Types
                          </Button>
                          <Button
                            type="button"
                            variant="secondary"
                            size="md"
                            onClick={() => setActiveView('county')}
                            disabled={!countyData}
                            className="border-accent text-muted-foreground hover:bg-accent hover:border-warning hover:text-warning hover:shadow-lg hover:shadow-warning/50"
                          >
                            By Incident County
                          </Button>
                        </div>
                      </div>
                    </div>
                  <HorizontalBarChart
                    data={(categoryData.data || []).slice(0, 10).map((item, index) => {
                      // Tab10 color palette (matplotlib tab10)
                      const tab10Colors = CHART_PALETTE;
                      return {
                        ...item,
                        label: redactCallType(item.label, isDemoMode), // Redact call type names
                        values: item.values.map(v => ({ 
                          ...v, 
                          vehicle: redactCallType(v.vehicle, isDemoMode), // Redact call type names in values
                          color: tab10Colors[index % tab10Colors.length] 
                        }))
                      };
                    })}
                    title=""
                    description=""
                    xAxisLabel={categoryData.xAxisLabel}
                    yAxisLabel={categoryData.yAxisLabel}
                    showPercentage={true}
                    height="h-auto"
                    yAxisLabelX={15}
                    leftPadding={150}
                  />
                </>
              )}

              {activeView === 'county' && countyData && (
                <>
                  <div>
                    <div className="flex items-center justify-between mb-6">
                      <div>
                        <h3 className="text-xl font-bold text-white mb-2">{getBusinessTitle('county', countyData.title)}</h3>
                        <p className="text-muted-foreground text-sm">
                          Identify which areas have the highest call volume. Use this to optimise station locations and ensure adequate coverage where it's needed most.
                        </p>
                      </div>
                    </div>
                    {/* Filters and Toggle Buttons */}
                    <div className="flex items-center gap-4 mb-4">
                        {/* Year and Month Dropdowns */}
                        <div className="flex items-center gap-4">
                          <div className="flex items-center gap-2">
                            <label className="text-sm font-medium text-muted-foreground">Year:</label>
                            <select
                              value={selectedYear}
                              onChange={(e) => setSelectedYear(e.target.value)}
                              onMouseDown={() => {
                                // Save current scroll position before dropdown opens
                                scrollPositionRef.current = window.scrollY || window.pageYOffset || document.documentElement.scrollTop || 0;
                              }}
                              onBlur={() => {
                                // Only restore scroll if it jumped to the top unexpectedly
                                // Check if current scroll is near top (within 100px) but we were scrolled down
                                const currentScroll = window.scrollY || window.pageYOffset || document.documentElement.scrollTop || 0;
                                const savedScroll = scrollPositionRef.current;
                                
                                // Only restore if we were scrolled down significantly and now we're near the top
                                if (savedScroll > 200 && currentScroll < 100) {
                                  // Restore the saved scroll position
                                  requestAnimationFrame(() => {
                                    window.scrollTo(0, savedScroll);
                                  });
                                }
                              }}
                              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all border cursor-pointer ${
                                selectedYear !== 'all'
                                  ? 'bg-white text-black border-warning shadow-lg shadow-warning/50'
                                  : 'bg-secondary text-muted-foreground border-accent hover:bg-accent hover:border-warning hover:text-warning hover:shadow-lg hover:shadow-warning/50'
                              } focus:outline-none focus:ring-2 focus:ring-warning focus:border-warning`}
                              style={{
                                appearance: 'none',
                                backgroundImage: selectedYear !== 'all'
                                  ? `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23000' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`
                                  : `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%239ca3af' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`,
                                backgroundRepeat: 'no-repeat',
                                backgroundPosition: 'right 0.75rem center',
                                paddingRight: '2.5rem'
                              }}
                            >
                              <option value="all" className="bg-card text-muted-foreground">All Years</option>
                              {availableYears.map(year => (
                                <option key={year} value={year.toString()} className="bg-card text-muted-foreground">
                                  {year}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div className="flex items-center gap-2">
                            <label className="text-sm font-medium text-muted-foreground">Month:</label>
                            <select
                              value={selectedMonth}
                              onChange={(e) => setSelectedMonth(e.target.value)}
                              onMouseDown={() => {
                                // Save current scroll position before dropdown opens
                                scrollPositionRef.current = window.scrollY || window.pageYOffset || document.documentElement.scrollTop || 0;
                              }}
                              onBlur={() => {
                                // Only restore scroll if it jumped to the top unexpectedly
                                // Check if current scroll is near top (within 100px) but we were scrolled down
                                const currentScroll = window.scrollY || window.pageYOffset || document.documentElement.scrollTop || 0;
                                const savedScroll = scrollPositionRef.current;
                                
                                // Only restore if we were scrolled down significantly and now we're near the top
                                if (savedScroll > 200 && currentScroll < 100) {
                                  // Restore the saved scroll position
                                  requestAnimationFrame(() => {
                                    window.scrollTo(0, savedScroll);
                                  });
                                }
                              }}
                              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all border cursor-pointer ${
                                selectedMonth !== 'all'
                                  ? 'bg-white text-black border-warning shadow-lg shadow-warning/50'
                                  : 'bg-secondary text-muted-foreground border-accent hover:bg-accent hover:border-warning hover:text-warning hover:shadow-lg hover:shadow-warning/50'
                              } focus:outline-none focus:ring-2 focus:ring-warning focus:border-warning`}
                              style={{
                                appearance: 'none',
                                backgroundImage: selectedMonth !== 'all'
                                  ? `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23000' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`
                                  : `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%239ca3af' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`,
                                backgroundRepeat: 'no-repeat',
                                backgroundPosition: 'right 0.75rem center',
                                paddingRight: '2.5rem'
                              }}
                            >
                              <option value="all" className="bg-card text-muted-foreground">All Months</option>
                              {availableMonths
                                .filter(m => selectedYear === 'all' || m.year.toString() === selectedYear)
                                .map(m => {
                                  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
                                  return (
                                    <option key={`${m.year}-${m.month}`} value={m.month.toString()} className="bg-card text-muted-foreground">
                                      {monthNames[m.month - 1]} {m.year}
                                    </option>
                                  );
                                })}
                            </select>
                          </div>
                        </div>
                        {/* Toggle Buttons */}
                        <div className="flex items-center gap-2 ml-auto">
                          <Button
                            type="button"
                            variant="secondary"
                            size="md"
                            onClick={() => setActiveView('category')}
                            disabled={!categoryData}
                            className="border-accent text-muted-foreground hover:bg-accent hover:border-warning hover:text-warning hover:shadow-lg hover:shadow-warning/50"
                          >
                            Call Types
                          </Button>
                          <Button
                            type="button"
                            variant="secondary"
                            size="md"
                            onClick={() => setActiveView('county')}
                            disabled={!countyData}
                            className="bg-white text-black border-warning shadow-lg shadow-warning/50"
                          >
                            By Incident County
                          </Button>
                        </div>
                      </div>
                    </div>
                  <HorizontalBarChart
                    data={(countyData.data || []).slice(0, 10).map((item, index) => {
                      // Tab10 color palette (matplotlib tab10)
                      const tab10Colors = CHART_PALETTE;
                      return {
                        ...item,
                        label: redactCounty(item.label, isDemoMode), // Redact county names
                        values: item.values.map(v => ({ 
                          ...v, 
                          vehicle: redactCounty(v.vehicle, isDemoMode), // Redact county names in values
                          color: tab10Colors[index % tab10Colors.length] 
                        }))
                      };
                    })}
                    title=""
                    description=""
                    xAxisLabel={countyData.xAxisLabel}
                    yAxisLabel="County"
                    showPercentage={true}
                    height="h-auto"
                  />
                </>
              )}

              {!loading && !categoryData && !countyData && (
                <div className="bg-card rounded-lg shadow-sm border border-border p-6 text-center">
                  <p className="text-muted-foreground">
                    Unable to load incident data. Please ensure the incident analysis JSON files are available in the backend.
                  </p>
                </div>
              )}
            </section>
          )}
        </>
      )}

      {useCase !== 'emergency' && (
        <div className="bg-card rounded-lg shadow-sm border border-border p-6 text-center">
          <p className="text-muted-foreground">
            The Incidents tab is only available in Emergency Services mode.
          </p>
        </div>
      )}

      {/* Only show Pumping Activity section when charts are ready (in emergency mode) or always (in non-emergency mode) */}
      {showPumpingActivity && (
        <section className="mt-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold text-white mb-2">Pumping Activity</h2>
              <p className="text-muted-foreground text-sm">
                Track pumping appliance usage. Helps plan equipment allocation and ensure good coverage for fire operations.
              </p>
            </div>
          </div>

          {pumpingLoading && (
            <div className="text-center py-8">
              <div className="flex flex-col items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-warning mb-4"></div>
                <p className="text-muted-foreground">Loading pumping data...</p>
              </div>
            </div>
          )}

          {!pumpingLoading && pumpingSummary.length === 0 && (
            <div className="bg-card rounded-lg shadow-sm border border-border p-6 text-center">
              <p className="text-muted-foreground">
                Pumping data not available. Please ensure the pumping analysis has been run.
              </p>
            </div>
          )}

          {!pumpingLoading && pumpingSummary.length > 0 && (
            <>
              {/* Tables - Full Width */}
              <div className="space-y-6">
                {/* Pumping Incidents by Vehicle */}
          <DataTable
                  title="Vehicle Summary"
            columns={[
              { key: 'vehicle', label: 'Vehicle' },
                    { key: 'years', label: 'Years', align: 'center' as const },
                    { key: 'calls', label: 'Total Calls', align: 'right' as const },
                    { key: 'avgDuration', label: 'Avg Pumping Duration', align: 'right' as const },
                    { key: 'dailyAverage', label: 'Daily Avg Calls', align: 'right' as const },
                    { key: 'weeklyAverage', label: 'Weekly Avg Calls', align: 'right' as const },
            ]}
            data={incidentData}
                  customColumnWidths={{
                    vehicle: { minWidth: 3, maxWidth: 4 },
                    years: { minWidth: 2, maxWidth: 3 },
                    calls: { minWidth: 3, maxWidth: 4 },
                    avgDuration: { minWidth: 4, maxWidth: 5 },
                    dailyAverage: { minWidth: 3, maxWidth: 4 },
                    weeklyAverage: { minWidth: 3, maxWidth: 4 }
                  }}
          />
        </div>

              {/* Pumping County Distribution Chart */}
              {pumpingLocationCalls.length > 0 && (
                <div className="mt-8">
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h3 className="text-xl font-bold text-white mb-2">Top 10 Counties by Pumping Activity</h3>
                      <p className="text-muted-foreground text-sm">
                        {pumpingCountyType === 'incident' 
                          ? 'View pumping activity distribution across incident counties (where incidents occurred).'
                          : 'View pumping activity distribution across response counties (where appliances were dispatched from).'
                        }
                        {' '}Filter by year and month to analyse trends. "Unknown" indicates locations where county data was unavailable.
                      </p>
                    </div>
                  </div>
                  
                  {/* Year and Month Filters */}
                  <div className="flex items-center gap-4 mb-4">
                      <div className="flex items-center gap-2">
                        <label className="text-sm font-medium text-muted-foreground">Year:</label>
                        <select
                          value={pumpingSelectedYear}
                          onChange={(e) => {
                            setPumpingSelectedYear(e.target.value);
                            setPumpingSelectedMonth('all'); // Reset month when year changes
                          }}
                          className="px-3 py-1.5 text-sm font-medium rounded-md transition-all border bg-secondary text-muted-foreground border-accent hover:bg-accent hover:border-warning hover:text-warning hover:shadow-lg hover:shadow-warning/50 focus:outline-none focus:ring-2 focus:ring-warning focus:border-warning cursor-pointer"
                          style={{
                            appearance: 'none',
                            backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%239ca3af' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`,
                            backgroundRepeat: 'no-repeat',
                            backgroundPosition: 'right 0.75rem center',
                            paddingRight: '2.5rem'
                          }}
                        >
                          <option value="all" className="bg-card text-muted-foreground">All Years</option>
                          {pumpingAvailableYears.map(year => (
                            <option key={year} value={year.toString()} className="bg-card text-muted-foreground">
                              {year}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="flex items-center gap-2">
                        <label className="text-sm font-medium text-muted-foreground">Month:</label>
                        <select
                          value={pumpingSelectedMonth}
                          onChange={(e) => setPumpingSelectedMonth(e.target.value)}
                          disabled={pumpingSelectedYear === 'all'}
                          className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all border ${
                            pumpingSelectedYear === 'all'
                              ? 'bg-card text-muted-foreground border-border cursor-not-allowed'
                              : 'bg-secondary text-muted-foreground border-accent hover:bg-accent hover:border-warning hover:text-warning hover:shadow-lg hover:shadow-warning/50 cursor-pointer'
                          } focus:outline-none focus:ring-2 focus:ring-warning focus:border-warning`}
                          style={{
                            appearance: 'none',
                            backgroundImage: pumpingSelectedYear === 'all' 
                              ? `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%234b5563' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`
                              : `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%239ca3af' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`,
                            backgroundRepeat: 'no-repeat',
                            backgroundPosition: 'right 0.75rem center',
                            paddingRight: '2.5rem'
                          }}
                        >
                          <option value="all" className="bg-card text-muted-foreground">All Months</option>
                          {pumpingAvailableMonths
                            .filter(m => pumpingSelectedYear === 'all' || m.year.toString() === pumpingSelectedYear)
                            .map(month => {
                              const monthName = new Date(month.year, month.month - 1).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
                              return (
                                <option key={`${month.year}-${month.month}`} value={`${month.year}-${month.month}`} className="bg-card text-muted-foreground">
                                  {monthName}
                                </option>
                              );
                            })}
                        </select>
                      </div>

                      {/* County Type Toggle Buttons */}
                      <div className="flex items-center gap-2 ml-auto">
                        <Button
                          type="button"
                          variant="secondary"
                          size="md"
                          onClick={() => setPumpingCountyType('response')}
                          className={`border-accent text-muted-foreground hover:bg-accent hover:border-warning hover:text-warning hover:shadow-lg hover:shadow-warning/50 ${
                            pumpingCountyType === 'response'
                              ? 'bg-white text-black border-warning shadow-lg shadow-warning/50'
                              : ''
                          }`}
                        >
                          Response Counties
                        </Button>
                        <Button
                          type="button"
                          variant="secondary"
                          size="md"
                          onClick={() => setPumpingCountyType('incident')}
                          className={`border-accent text-muted-foreground hover:bg-accent hover:border-warning hover:text-warning hover:shadow-lg hover:shadow-warning/50 ${
                            pumpingCountyType === 'incident'
                              ? 'bg-white text-black border-warning shadow-lg shadow-warning/50'
                              : ''
                          }`}
                        >
                          Incident Counties
                        </Button>
                      </div>
                    </div>
                  
                  {pumpingCountyData.length > 0 ? (
                    <HorizontalBarChart
                      data={pumpingCountyData}
                      title=""
                      description="Distribution of pumping incidents across counties"
                      xAxisLabel="No. of Pumping Calls"
                      yAxisLabel="County"
                      showPercentage={true}
                      height="h-107"
                      leftPadding={150}
                    />
                  ) : (
                    <div className="bg-card rounded-lg shadow-sm border border-border p-6 text-center">
                      <p className="text-muted-foreground">No pumping data available for the selected filters.</p>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
      </section>
      )}
    </div>
  );
}

